import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { formatBool, formatDate } from "../../../../utils";
import { TableField, PageData } from "../../../types";
import {
    GetManifestsRequest,
    ManifestListFilters,
    ManifestResource,
    ManifestListItem,
    ManifestDetailListItem,
    GetAvailableOrdersRequest,
    AvailableBarcodeOrderListItem
  } from "../../../../models/api/barcodeSystem/manifests/get";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { AxiosResponse } from "axios";
import { defaultManifest, defaultManifestFilters } from "./helpers";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import notifications from "@/lib/notifications";
import _ from "lodash";
import * as modals from "@/lib/modals";
import { BarcodeOrderListFilters } from "../../../../models/api/barcodeSystem/barcodeOrders/get";
import { CreateManifestRequest } from "../../../../models/api/barcodeSystem/manifests/create";
import { UpdateManifestRequest } from "../../../../models/api/barcodeSystem/manifests/update";
import { defaultBarcodeOrderFilters } from "../barcodeOrder/helpers";
import { ImportManifestRequest, ImportManifestResponse } from "@/models/api/barcodeSystem/manifests/importManifests";

const name = "manifest";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class ManifestModule extends VuexModule {
    public currentManifest: ManifestResource = defaultManifest();
    public currentManifestDetails: ManifestDetailListItem[] = [];
    public availableOrders: AvailableBarcodeOrderListItem[] = [];
    public data?: ManifestListItem[] = null;
    public filters: ManifestListFilters = defaultManifestFilters();
    public availableOrdersFilters: BarcodeOrderListFilters = defaultBarcodeOrderFilters();
    public dataSortKey: string = null;
    public dataSortAscending: boolean = true;
    public pageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 50
    };
    public fields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "manifestName", sortable: true, label: "Manifest Name", hidden: false },
        { key: "deliveryDate", sortable: true, label: "Delivery Date", formatter: formatDate, hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "tripDays", sortable: true, label: "Trip Days", hidden: false },
        { key: "createdDate", sortable: true, label: "Created Date", formatter: formatDate, hidden: false},
        { key: "lastUpdatedDate", sortable: true, label: "Update Date", formatter: formatDate, hidden: false},
        { key: "lastUpdatedBy", sortable: true, label: "Updated By", hidden: false},
        { key: "isWillCallManifest", sortable: true, label: "Willcall", formatter: formatBool, hidden: false}
    ];
    public loading: boolean = false;

    @Action
    async getManifests(params: GetManifestsRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetList();
      }
  
      let target = `/barcode/manifests/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.filters) {
        if (this.filters.id) {
            target += `&id=${this.filters.id}`;
        }
        if (this.filters.manifestName) {
            target += `&manifestName=${this.filters.manifestName}`;
        }
        if (this.filters.isViewable !== undefined && this.filters.isViewable != null) {
            target += `&isViewable=${this.filters.isViewable}`;
        }
        if (this.filters.isLocked !== undefined && this.filters.isLocked != null) {
            target += `&isLocked=${this.filters.isLocked}`;
        }
        if (this.filters.deliveryDate) {
            const dateData = this.filters.deliveryDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveryDateStart=${dateData[0]}&deliveryDateEnd=${dateData[1]}`;
        }
        if (this.filters.createdDate) {
            const dateData = this.filters.createdDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&createdDateStart=${dateData[0]}&createdDateEnd=${dateData[1]}`;
        }
        if (this.filters.lastUpdatedDate) {
            const dateData = this.filters.lastUpdatedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&lastUpdatedDateStart=${dateData[0]}&lastUpdatedDateEnd=${dateData[1]}`;
        }
        if (this.filters.facilityCodes) {
          this.filters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
            target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.filters.actualFacilityCodes) {
          this.filters.actualFacilityCodes.forEach(element => {
            target += `&actualFacilityCodes=${element}`;
          });
        }
        if (this.filters.isWillCallManifest) {
            target += `&isWillCallManifest=${this.filters.isWillCallManifest}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.dataSortKey) {
        target += `&sortField=${this.dataSortKey}&SortAscending=${
          this.dataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<ManifestListItem[]>(target);
        this.setList(response);
        this.setCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async getAvailableOrders(params: GetAvailableOrdersRequest): Promise<void> {
      this.resetAvailableOrders();
      
      let target = `/barcode/manifests/availableOrders?facilityCode=${params.facilityCode}&deliveryDate=${params.deliveryDate}&tripDays=${params.tripDays}`;

      if(params.onActiveManifest != null)
      {
        target += `&onActiveManifest=${params.onActiveManifest}`;
      }
  
      if (this.availableOrdersFilters) {
        if (this.availableOrdersFilters.id) {
          target += `&id=${this.availableOrdersFilters.id}`;
        }
        if (this.availableOrdersFilters.loadId) {
          target += `&loadId=${this.availableOrdersFilters.loadId}`;
        }
        if (this.availableOrdersFilters.orderNumber) {
          target += `&orderNumber=${this.availableOrdersFilters.orderNumber}`;
        }
        if (this.availableOrdersFilters.crn) {
            target += `&crn=${this.availableOrdersFilters.crn}`;
        }
        if (this.availableOrdersFilters.statuses) {
          this.availableOrdersFilters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.availableOrdersFilters.deliveryTypes) {
          this.availableOrdersFilters.deliveryTypes.forEach(element => {
            target += `&deliveryTypes=${element}`;
          });
        }
        if (this.availableOrdersFilters.actualFacilityCodes) {
          this.availableOrdersFilters.actualFacilityCodes.forEach(element => {
            target += `&actualFacilityCodes=${element}`;
          });
        }
        if (this.availableOrdersFilters.billToCompanyIds) {
          this.availableOrdersFilters.billToCompanyIds.forEach(element => {
            target += `&billToCompanyIds=${element}`;
          });
        }
        if (this.availableOrdersFilters.companyIds) {
          this.availableOrdersFilters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.availableOrdersFilters.customer) {
          target += `&customer=${this.availableOrdersFilters.customer}`;
        }
        if (this.availableOrdersFilters.orderGroupText) {
          target += `&orderGroupText=${this.availableOrdersFilters.orderGroupText}`;
        }
        if (this.availableOrdersFilters.orderGroupText2) {
          target += `&orderGroupText2=${this.availableOrdersFilters.orderGroupText2}`;
        }
        if (this.availableOrdersFilters.relatedOrdersCRN) {
          target += `&relatedOrdersCRN=${this.availableOrdersFilters.relatedOrdersCRN}`;
        }
        if (this.availableOrdersFilters.esnIdentity) {
          target += `&esnIdentity=${this.availableOrdersFilters.esnIdentity}`;
        }
        if (this.availableOrdersFilters.noScanWithItems) {
          target += `&noScanWithItems=${this.availableOrdersFilters.noScanWithItems}`;
        }
        if (this.availableOrdersFilters.isOpen) {
          target += `&isOpen=${this.availableOrdersFilters.isOpen}`;
        }
        if (this.availableOrdersFilters.requiresReceiving) {
          target += `&requiresReceiving=${this.availableOrdersFilters.requiresReceiving}`;
        }
        if (this.availableOrdersFilters.requiresPicking) {
          target += `&requiresPicking=${this.availableOrdersFilters.requiresPicking}`;
        }
        if (this.availableOrdersFilters.requiresDelivery) {
          target += `&requiresDelivery=${this.availableOrdersFilters.requiresDelivery}`;
        }
        if (this.availableOrdersFilters.isPuc) {
          target += `&isPuc=${this.availableOrdersFilters.isPuc}`;
        }
        if (this.availableOrdersFilters.isPud) {
          target += `&requiresReceiving=${this.availableOrdersFilters.isPud}`;
        }
        if (this.availableOrdersFilters.isLinkingComplete) {
          target += `&isLinkingComplete=${this.availableOrdersFilters.isLinkingComplete}`;
        }
        if (this.availableOrdersFilters.isPrescheduled) {
          target += `&requiresReceiving=${this.availableOrdersFilters.isPrescheduled}`;
        }
        if (this.availableOrdersFilters.importedDate) {
            const dateData = this.availableOrdersFilters.importedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&importedDateStart=${dateData[0]}&importedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.receivedDate) {
            const dateData = this.availableOrdersFilters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.loadedDate) {
            const dateData = this.availableOrdersFilters.loadedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadedDateStart=${dateData[0]}&loadedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.deliveredDate) {
            const dateData = this.availableOrdersFilters.deliveredDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveredDateStart=${dateData[0]}&deliveredDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.receivingProcessedDate) {
            const dateData = this.availableOrdersFilters.receivingProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivingProcessedDateStart=${dateData[0]}&receivingProcessedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.loadingProcessedDate) {
            const dateData = this.availableOrdersFilters.loadingProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&loadingProcessedDateStart=${dateData[0]}&loadingProcessedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.deliveryProcessedDate) {
            const dateData = this.availableOrdersFilters.deliveryProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&deliveryProcessedDateStart=${dateData[0]}&deliveryProcessedDateEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.earliestAppt) {
            const dateData = this.availableOrdersFilters.earliestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&earliestApptStart=${dateData[0]}&earliestApptEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.latestAppt) {
            const dateData = this.availableOrdersFilters.latestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&latestApptStart=${dateData[0]}&latestApptEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.originalEarliestAppt) {
            const dateData = this.availableOrdersFilters.originalEarliestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&originalEarliestApptStart=${dateData[0]}&originalEarliestApptEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.originalLatestAppt) {
            const dateData = this.availableOrdersFilters.originalLatestAppt.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&originalLatestApptStart=${dateData[0]}&originalLatestApptEnd=${dateData[1]}`;
        }
        if (this.availableOrdersFilters.receivingUsername) {
          target += `&receivingUsername=${this.availableOrdersFilters.receivingUsername}`;
        }
        if (this.availableOrdersFilters.loadingUsername) {
          target += `&loadingUsername=${this.availableOrdersFilters.loadingUsername}`;
        }
        if (this.availableOrdersFilters.deliveryUsername) {
          target += `&deliveryUsername=${this.availableOrdersFilters.deliveryUsername}`;
        }
      }
      
      try {
        var response = await rawApi.get<AvailableBarcodeOrderListItem[]>(target);
        this.setAvailableOrders(response);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async applyListSort(sort: string) {     
      this.setDataSort(sort);
      await this.getManifests({
          pageNumber: 1,
          pageSize: this.pageData.pageSize
      });
    }

    @Action
    toggleField(fieldChange) {
      this.setFieldStatus(fieldChange);
    }

    @Action
    async getManifest(id): Promise<ManifestResource> {
      const response = await rawApi.get<ManifestResource>(`/barcode/manifests/${id}`);
      return response.data;
    }

    @Action
    async getManifestDetails(id): Promise<ManifestDetailListItem[]> {
      const response = await rawApi.get<ManifestDetailListItem[]>(`/barcode/manifests/${id}/details`);
      return response.data;
    }

    @Action 
    resetData() {
        this.resetList();
    }

    @Action
    async loadManifest(id: number): Promise<void> {
      const data = await this.getManifest(id);
      this.setCurrentManifest(data);
      this.refreshManifestData(data);
    }   
     
    @Action
    async loadManifestDetails(id: number): Promise<void> {
      this.setCurrentManifestDetails(await this.getManifestDetails(id));
    }

    @Action
    async loadAvailableOrders(onActiveManifest: boolean): Promise<void> {
      if(this.currentManifest && this.currentManifest.deliveryDate && this.currentManifest.facilityCode) {
        let request = {
          deliveryDate: this.currentManifest.deliveryDate,
          tripDays: this.currentManifest.tripDays ? this.currentManifest.tripDays : 1,
          facilityCode: this.currentManifest.facilityCode,
          onActiveManifest: onActiveManifest
        } as GetAvailableOrdersRequest;
        await this.getAvailableOrders(request);
      }
      else {  
        this.resetAvailableOrders();
      }
    }
  
    @Action
    async reloadManifest(): Promise<void> {
      if (this.currentManifest)
      {
        const data = await this.getManifest(this.currentManifest.id);
        this.setCurrentManifest(data);
        this.refreshManifestData(data);
      } 
    }    
    
    @Action
    async startNew(): Promise<void> {
      this.resetCurrentManifest();
      this.updateCurrentManifestName('');
      this.updateCurrentManifestDeliveryDate(new Date().toJSON());
      this.updateCurrentManifestFacilityCode(barcodeSystemModuleState.currentFacilityCode);
      this.updateCurrentManifestTripDays(1);
      this.updateCurrentManifestIsWillCallManifest(false);  
      this.updateCurrentManifestIsViewable(true);  
    }

    @Action
    async saveManifest(): Promise<void> {
      if (this.currentManifest)
      {
        if(this.currentManifest.id) {
          const updateRequest = {
            id: this.currentManifest.id,
            facilityCode: this.currentManifest.facilityCode,
            manifestName: this.currentManifest.manifestName,
            deliveryDate: this.currentManifest.deliveryDate,
            tripDays: this.currentManifest.tripDays,
            isWillCallManifest: this.currentManifest.isWillCallManifest,
            orderIds: this.currentManifestDetails ? this.currentManifestDetails.map(function(x) { return x.orderId}) : []
          } as UpdateManifestRequest;
          await api.putAsync(`barcode/manifests/${this.currentManifest.id}`, updateRequest)
            .catch(error => {throw error});
        }
        else {
          const createRequest = {
            facilityCode: this.currentManifest.facilityCode,
            manifestName: this.currentManifest.manifestName,
            deliveryDate: this.currentManifest.deliveryDate,
            tripDays: this.currentManifest.tripDays,
            isWillCallManifest: this.currentManifest.isWillCallManifest,
            orderIds: this.currentManifestDetails ? this.currentManifestDetails.map(function(x) { return x.orderId}) : []
          } as CreateManifestRequest;
          await api.postAsync(`barcode/manifests`, createRequest)
            .then(res => {
              this.setCurrentManifestId(res.data.resource.id);
            })
            .catch(error => {throw error})
        }
      } 
    }

    @Action
    async importManifests(
      request: ImportManifestRequest
    ): Promise<ImportManifestResponse> {
      const formData = new FormData();
  
      formData.append("file", request.file);
      const response = await rawApi.post<ImportManifestResponse>(
        `/barcode/manifests/import?facilityCode=${request.facilityCode}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
  
      return response.data;
    }

    @Action
    async viewManifestShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.manifestViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }     
    
    @Action
    async newManifestShortcutAction() {
      return this.context.dispatch(
        "system/showModal",
        { name: modals.manifestViewModal, data: { new: true } },
        { root: true }
      );
    }    
    
    @Action
    async hideManifestShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.hideManifestModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }
    
    @Action
    async restoreManifestShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.restoreManifestModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }

    @Action
    async updateManifestsAfterAction(items: any) {
      for (let index = 0; index < items.length; index++) {
        const load = await this.getManifest(items[index].id);
        this.refreshManifestData(load)
      }
    }

    @Action
    async createManifest(payload: CreateManifestRequest) {
      await api.putAsync(`barcode/manifests`, payload);
    }  
    
    @Action
    async updateManifest(payload: UpdateManifestRequest) {
      await api.postAsync(`barcode/manifests/${payload.id}`, payload);
    }

    @Action
    async hideManifest(manifest) {
      await api.postAsync(`barcode/manifests/${manifest.id}/hide`, manifest);
    }

    @Action
    async restoreManifest(manifest) {
      await api.postAsync(`barcode/manifests/${manifest.id}/restore`, manifest);
    }

    @Mutation
    setLoading(isLoading: boolean) {
      this.loading = isLoading;
    }

    @Mutation
    setFilters(filters: ManifestListFilters) {
      this.filters = filters;
    }

    @Mutation
    setAvailableOrdersFilters(filters: BarcodeOrderListFilters) {
      this.availableOrdersFilters = filters;
    }

    @Mutation
    setAvailableOrders(response: AxiosResponse<AvailableBarcodeOrderListItem[]>) {
      this.availableOrders = response.data;
    }

    @Mutation
    setList(response: AxiosResponse<ManifestListItem[]>) {
      this.pageData.totalItems = response.headers.totalitems;
      this.pageData.totalPages = response.headers.totalpages;
      if (this.data) {
          this.data = this.data.concat(response.data);
      } else {
          this.data = response.data;
      }
    }  
    
    @Mutation
    setCurrentPage(number: number) {
      this.pageData.currentPage = number;
    }

    @Mutation
    resetList() {
      this.pageData.totalItems = 0;
      this.pageData.totalPages = 0;
      this.data = null;
    }
    
    @Mutation
    resetAvailableOrders() {
      this.availableOrders = [];
    }

    @Mutation
    switchSelectionStatus(id: number) {
      var index = this.data.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.data[index];

      existing.selected = !existing.selected;

      this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
    }  

    @Mutation
    switchManifestDetailSelectionStatus(orderId: number) {
      if(this.currentManifestDetails)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentManifestDetails[index];

        existing.selected = !existing.selected;

        this.currentManifestDetails = [...this.currentManifestDetails.slice(0, index), existing, ...this.currentManifestDetails.slice(index + 1)];
      }    
    }       
    
    @Mutation
    updateCurrentManifestId(value: number) {
      this.currentManifest.id = value;
    } 
    
    @Mutation
    updateCurrentManifestName(value: string) {
      this.currentManifest.manifestName = value;
    }      
    
    @Mutation
    updateCurrentManifestFacilityCode(value: string) {
      this.availableOrders = [];
      this.currentManifest.facilityCode = value;
    }    
    
    @Mutation
    updateCurrentManifestDeliveryDate(value: string) {
      this.availableOrders = [];
      this.currentManifest.deliveryDate = value;
    }        
    
    @Mutation
    updateCurrentManifestTripDays(value: number) {
      this.availableOrders = [];
      this.currentManifest.tripDays = value;
    }   

    @Mutation
    updateCurrentManifestIsWillCallManifest(value: boolean) {
      this.currentManifest.isWillCallManifest = value;
    }     

    @Mutation
    updateCurrentManifestIsViewable(value: boolean) {
      this.currentManifest.isViewable = value;
    }     
    
    @Mutation
    removeManifestDetail(orderId: number) {
      if(this.currentManifestDetails)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index < 0) {
            return;
        }
        this.currentManifestDetails = [...this.currentManifestDetails.slice(0, index), ...this.currentManifestDetails.slice(index + 1)];
      }    
    } 
    
    @Mutation
    addManifestDetail(order: AvailableBarcodeOrderListItem) {
      if(!this.currentManifestDetails)
        this.currentManifestDetails = [];

      if(order)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === order.id;
        });

        if (index >= 0) {
            return;
        }

        const newDetail = {
          selected: false,
          id: 0,
          orderId: order.id,
          order: order,
          picked: false,
          staged: false,
          checked: false,
          pickedBy: '',
          stagedBy: '',
          checkedBy: ''
        } as ManifestDetailListItem

        this.currentManifestDetails.push(newDetail);
      }    
    }  
    
    @Mutation
    moveManifestDetailTop(orderId: number) {
      if(this.currentManifestDetails && this.currentManifestDetails.length > 1)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index <= 0) {
            return;
        }

        const moveTop = this.currentManifestDetails[index];
        this.currentManifestDetails = [moveTop, ...this.currentManifestDetails.slice(0, index), ...this.currentManifestDetails.slice(index + 1)];
      }    
    }

    @Mutation
    moveManifestDetailUp(orderId: number) {
      if(this.currentManifestDetails && this.currentManifestDetails.length > 1)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index <= 0) {
            return;
        }

        const moveUp = this.currentManifestDetails[index];
        this.currentManifestDetails = [...this.currentManifestDetails.slice(0, index -1), moveUp, this.currentManifestDetails[index - 1], ...this.currentManifestDetails.slice(index + 1)];
      }    
    }

    @Mutation
    moveManifestDetailDown(orderId: number) {
      if(this.currentManifestDetails && this.currentManifestDetails.length > 1)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index < 0 || index >= (this.currentManifestDetails.length - 1)) {
            return;
        }

        const moveDown = this.currentManifestDetails[index];
        this.currentManifestDetails = [...this.currentManifestDetails.slice(0, index), this.currentManifestDetails[index + 1], moveDown, ...this.currentManifestDetails.slice(index + 2)];
      }    
    }

    @Mutation
    moveManifestDetailBottom(orderId: number) {
      if(this.currentManifestDetails && this.currentManifestDetails.length > 1)
      {
        var index = this.currentManifestDetails.findIndex(function(item) {
          return item.orderId === orderId;
        });

        if (index < 0 || index >= (this.currentManifestDetails.length - 1)) {
            return;
        }

        const moveBottom = this.currentManifestDetails[index];
        this.currentManifestDetails = [...this.currentManifestDetails.slice(0, index), ...this.currentManifestDetails.slice(index + 1), moveBottom];
      }    
    }

    @Mutation
    refreshManifestData(manifest: ManifestListItem) {
      if(this.data)
      {
        var index = this.data.findIndex(function(item) {
            return item.id === manifest.id;
        });

        if (index < 0) {
            return;
        }

        this.data = [...this.data.slice(0, index), manifest, ...this.data.slice(index + 1)]
      }
    } 
    
    @Mutation
    setCurrentManifest(data) {
      this.loading = false;
      this.currentManifestDetails = [];
      this.currentManifest = data;
      this.availableOrders = [];
    }    
    
    @Mutation
    setCurrentManifestDetails(data) {
      this.loading = false;
      this.currentManifestDetails = data;
    }

    @Mutation
    resetCurrentManifest() {
      this.currentManifest = defaultManifest();
      this.currentManifestDetails = [];
      this.availableOrders = [];
    }

    @Mutation
    setFieldStatus(manifestFieldChange) {
      this.fields[manifestFieldChange.idx].hidden = manifestFieldChange.value;
    }

    @Mutation
    setDataSort(key) {
      if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
      else {
        this.dataSortKey = key;
        this.dataSortAscending = true;
      }
    }

    @Mutation
    setCurrentManifestId(id) {
      if (this.currentManifest) 
        this.currentManifest.id = id;
    }
    

    get getSelectedItems(): Array<ManifestListItem> {
        if (!this.data) return [];
        return this.data.filter(x => x.selected);
    }

    get getSelectedItemCount(): number {
        return this.getSelectedItems.length;
    }

    get getSelectedManifestDetails(): Array<ManifestDetailListItem> {
      if (!this.currentManifestDetails) return [];
      return this.currentManifestDetails.filter(x => x.selected);
    }

    get getSelectedManifestDetailCount(): number {
      return this.getSelectedManifestDetails.length;
    }
}